/**
 * Gang Sheet Builder - Cart Preview/Edit Buttons
 * DripApps builder-button script fallback
 * Fetches cart items and adds Preview/Edit buttons for gang sheet products
 */

(function() {
  'use strict';

  const GSButtons = {
    debug: false,
    
    log(...args) {
      if (this.debug) console.log('[GS-Buttons]', ...args);
    },

    init() {
      this.log('Initializing...');
      
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.processCart());
      } else {
        this.processCart();
      }

      // Also listen for cart updates (AJAX cart changes)
      this.observeCartChanges();
    },

    async processCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        this.log('Cart fetched:', cart.items.length, 'items');
        
        // Small delay to ensure DOM is rendered
        setTimeout(() => {
          this.addButtonsToCart(cart.items);
        }, 100);
        
      } catch (error) {
        console.error('[GS-Buttons] Error fetching cart:', error);
      }
    },

    addButtonsToCart(items) {
      // Find all cart line items in DOM
      const lineItemElements = document.querySelectorAll('.cart-line-item[data-key]');
      
      this.log('Found', lineItemElements.length, 'line item elements');

      items.forEach((item, index) => {
        // Check if item has Preview or Edit properties
        if (!item.properties) return;
        
        const previewUrl = item.properties['Preview'];
        const editUrl = item.properties['Edit'];
        
        if (!previewUrl && !editUrl) return;
        
        this.log(`Item ${index} has gang sheet properties:`, { previewUrl, editUrl });

        // Find matching DOM element by key
        const itemKey = item.key;
        const lineItemEl = document.querySelector(`.cart-line-item[data-key="${itemKey}"]`);
        
        if (!lineItemEl) {
          this.log(`No DOM element found for key: ${itemKey}`);
          return;
        }

        // Check if buttons already exist
        if (lineItemEl.querySelector('.gs-buttons-container')) {
          this.log('Buttons already exist for:', itemKey);
          return;
        }

        // Find the details container to insert buttons
        const detailsContainer = lineItemEl.querySelector('.line-item-details') || 
                                  lineItemEl.querySelector('.line-item-info') ||
                                  lineItemEl.querySelector('.cart-item__details');

        if (!detailsContainer) {
          this.log('No details container found for:', itemKey);
          return;
        }

        // Create buttons container
        const buttonsContainer = this.createButtonsContainer(previewUrl, editUrl);
        
        // Insert after variant options or at the end of details
        const variantOptions = detailsContainer.querySelector('.variant-options');
        const priceContainer = detailsContainer.querySelector('.line-item-pricing');
        
        if (priceContainer) {
          priceContainer.before(buttonsContainer);
        } else if (variantOptions) {
          variantOptions.after(buttonsContainer);
        } else {
          detailsContainer.appendChild(buttonsContainer);
        }

        this.log('Buttons added for:', item.title);
      });
    },

    createButtonsContainer(previewUrl, editUrl) {
      const container = document.createElement('div');
      container.className = 'gs-buttons-container';
      container.style.cssText = `
        display: flex;
        gap: 8px;
        margin: 10px 0;
        flex-wrap: wrap;
      `;

      if (previewUrl) {
        const previewBtn = document.createElement('a');
        previewBtn.href = previewUrl;
        previewBtn.target = '_blank';
        previewBtn.rel = 'noopener noreferrer';
        previewBtn.className = 'gs-btn gs-btn-preview';
        previewBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          <span>Preview</span>
        `;
        container.appendChild(previewBtn);
      }

      if (editUrl) {
        const editBtn = document.createElement('a');
        editBtn.href = editUrl;
        editBtn.target = '_blank';
        editBtn.rel = 'noopener noreferrer';
        editBtn.className = 'gs-btn gs-btn-edit';
        editBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
          <span>Edit Design</span>
        `;
        container.appendChild(editBtn);
      }

      return container;
    },

    observeCartChanges() {
      // MutationObserver to detect cart updates
      const cartContainer = document.querySelector('.cart-items, .cart-table, #cart-items, [data-cart-items]');
      
      if (cartContainer) {
        const observer = new MutationObserver((mutations) => {
          // Debounce - wait for mutations to settle
          clearTimeout(this.mutationTimeout);
          this.mutationTimeout = setTimeout(() => {
            this.log('Cart DOM changed, reprocessing...');
            this.processCart();
          }, 300);
        });

        observer.observe(cartContainer, {
          childList: true,
          subtree: true
        });

        this.log('MutationObserver attached to cart container');
      }

      // Also listen for common cart update events
      document.addEventListener('cart:updated', () => this.processCart());
      document.addEventListener('cart:refresh', () => this.processCart());
      
      // Shopify Section rendering
      document.addEventListener('shopify:section:load', () => this.processCart());
    }
  };

  // Add CSS styles
  const style = document.createElement('style');
  style.textContent = `
    .gs-buttons-container {
      animation: gsFadeIn 0.3s ease;
    }
    
    @keyframes gsFadeIn {
      from { opacity: 0; transform: translateY(-5px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    .gs-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.2s ease;
      cursor: pointer;
      border: none;
    }
    
    .gs-btn svg {
      flex-shrink: 0;
    }
    
    .gs-btn-preview {
      background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
      color: #fff;
      box-shadow: 0 2px 8px rgba(0, 86, 204, 0.3);
    }
    
    .gs-btn-preview:hover {
      background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 86, 204, 0.4);
      color: #fff;
    }
    
    .gs-btn-edit {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      color: #fff;
      box-shadow: 0 2px 8px rgba(240, 147, 251, 0.3);
    }
    
    .gs-btn-edit:hover {
      background: linear-gradient(135deg, #e879f9 0%, #ec4899 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(240, 147, 251, 0.4);
      color: #fff;
    }

    /* Mobile responsive */
    @media (max-width: 768px) {
      .gs-buttons-container {
        flex-direction: column;
        gap: 6px;
      }
      
      .gs-btn {
        justify-content: center;
        padding: 10px 16px;
        font-size: 14px;
      }
    }
  `;
  document.head.appendChild(style);

  // Initialize
  GSButtons.init();

})();
